import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { SectionLabel } from './SectionLabel';
import { PRODUCTS, STATS } from '../data/site';
import type { ProductStatus } from '../data/site';
const EASE = [0.23, 1, 0.32, 1] as const;
export function HomeHero() {
  const reduce = useReducedMotion();
  return <section className="border-b border-bronze-200 bg-bronze-50" data-id="element-60" data-mp="y2bk2d25z8ib">
      <div className="mx-auto max-w-container px-6 lg:px-10" data-id="element-61" data-mp="12ta67t9u30th">
        {/* Headline + mark */}
        <div className="grid items-center gap-12 py-16 lg:grid-cols-[1fr_auto] lg:gap-10 lg:py-20" data-id="element-62" data-mp="p1vl9gwd8pjs">
          <motion.div initial={reduce ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="max-w-2xl" data-id="element-63" data-mp="u0zu26fdol9l">
            <SectionLabel data-id="element-64" data-mp="k326ll5cesi1">Software for African operators</SectionLabel>
            <h1 className="mt-7 text-5xl font-semibold leading-none tracking-tightest text-ink-950 sm:text-6xl lg:text-[4.25rem]" data-id="element-65" data-mp="1hurz2zqvhveq">
              The systems
              <br data-id="element-66" data-mp="1cv5komzb694c" />
              your business
              <br data-id="element-67" data-mp="1qv6mc4nedkkv" />
              rests on.
            </h1>
            <p className="mt-7 max-w-[32.5rem] text-base leading-relaxed text-ink-600" data-id="element-68" data-mp="1qunejgrgockk">
              Keystone builds platforms for the work Africa runs on — property
              management, land records, and community finance. Our first product,
              PropFlow, is live: one platform for a whole portfolio, with AI
              workflows that make sure nothing is forgotten.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-4" data-id="element-69" data-mp="17xvo8ndi4x70">
              <Link to="/propflow" className="group inline-flex items-center gap-2 whitespace-nowrap bg-bronze-500 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-bronze-600" data-id="element-70" data-mp="1352ko2trruwi">
                Explore PropFlow
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-71" data-mp="2aiwta6qhxdml" />
              </Link>
              <Link to="/products" className="whitespace-nowrap border-b border-bronze-500 pb-1 text-sm font-semibold text-ink-950 transition-colors duration-150 ease-out hover:text-bronze-600" data-id="element-72" data-mp="2ka154xkvkid7">
                See what is coming
              </Link>
            </div>
          </motion.div>

          <KeystoneMark data-id="element-73" data-mp="21j1abfqp8ec0" />
        </div>

        {/* Typographic index */}
        <div className="grid gap-12 border-t border-bronze-200 py-10 lg:grid-cols-[46fr_54fr] lg:gap-0" data-id="element-74" data-mp="21l4cey54lxx3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8" data-id="element-75" data-mp="26fi2cl10rcud">
            {STATS.map(stat => <div key={stat.label} data-id="element-76" data-mp="3d09ebzxfnsfn">
                <dt className="sr-only" data-id="element-77" data-mp="3hw8a48l3ccq3">{stat.label}</dt>
                <dd data-id="element-78" data-mp="3hyuho87zes8b">
                  <span className="block text-[1.75rem] font-semibold leading-tight tracking-tight text-ink-950" data-id="element-79" data-mp="169ejepx8hz6m">
                    {stat.value}
                  </span>
                  <span className="mt-2 block max-w-[11rem] text-xs uppercase leading-normal tracking-[0.02em] text-ink-500" data-id="element-80" data-mp="11co2k0dluzk5">
                    {stat.label}
                  </span>
                </dd>
              </div>)}
          </dl>

          <div className="lg:border-l lg:border-bronze-200 lg:pl-12" data-id="element-81" data-mp="mmqavaggvrq7">
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bronze-600" data-id="element-82" data-mp="ikbr8o4z4nxv">
              What we're building
            </h2>
            <ol className="mt-4" data-id="element-83" data-mp="dlio9pnbcfby">
              {PRODUCTS.map((product, index) => <li key={product.slug} className="border-t border-bronze-100 first:border-t-0" data-id="element-84" data-mp="8n7f7h4ue8us" data-mpi={index}>
                  <Link to={product.to ?? `/products#${product.slug}`} className="group flex flex-wrap items-baseline gap-x-4 gap-y-1 py-3 sm:flex-nowrap" data-id="element-85" data-mp="22zx93yv7ni26">
                    <span className="w-5 shrink-0 text-xs font-semibold text-bronze-300" data-id="element-86" data-mp="14omb29586wf8">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <span className="w-32 shrink-0 text-base font-semibold tracking-tight text-ink-950 transition-colors duration-150 ease-out group-hover:text-bronze-600" data-id="element-87" data-mp="qqpfng2icw25">
                      {product.name}
                    </span>
                    <span className="min-w-0 flex-1 text-[0.8rem] text-ink-500" data-id="element-88" data-mp="lpi210dop2nk">
                      {product.category}
                    </span>
                    <span className={`shrink-0 whitespace-nowrap text-[0.66rem] font-bold uppercase tracking-[0.08em] ${statusColor(product.status)}`} data-id="element-89" data-mp="7vqvm0v91yw2">
                      {statusText(product.status, product.timing)}
                    </span>
                  </Link>
                </li>)}
            </ol>
          </div>
        </div>
      </div>
    </section>;
}
function KeystoneMark() {
  return <svg viewBox="0 0 340 362" className="hidden h-auto w-[300px] shrink-0 lg:block xl:w-[340px]" aria-hidden="true" focusable="false" data-id="element-90" data-mp="3bj43kx4poi2s">
      <polygon points="124,0.5 256,0.5 339.5,361.5 40.5,361.5" fill="none" stroke="#cfab84" strokeWidth="1" data-id="element-91" data-mp="3kfuwx1rvpito" />
      <polygon points="140,76 240,76 294,324 86,324" fill="#f1e6d9" data-id="element-92" data-mp="3ufvher1hi4zg" />
      <polygon points="159,168 221,168 250,312 130,312" fill="#a9713b" data-id="element-93" data-mp="3udj1skj786pk" />
      <rect x="185" y="252" width="10" height="10" fill="#0d141b" data-id="element-94" data-mp="c0b94i9z8sm0" />
    </svg>;
}
function statusColor(status: ProductStatus) {
  if (status === 'Live') return 'text-bronze-600';
  if (status === 'In development') return 'text-ink-600';
  return 'text-ink-500';
}
function statusText(status: ProductStatus, timing: string) {
  if (status === 'In development') return `${status}, ${timing.toLowerCase()}`;
  return status;
}